import React, { useState } from "react";

import {
  Badge,
  Button,
  Container,
  Image,
  Nav,
  Navbar,
  NavDropdown,
} from "react-bootstrap";
import { NavLink, useLocation } from "react-router-dom";

import { getCounterpartPath } from "@/utils/i18nRoutes";
import { getCurrentLang, getHomeUrl, getUrlLangPrefix } from "@/utils/Local";

import FaCart from "../assets/icons/faCart.svg";
import useCart from "../hooks/useCart";
import i18n from "../i18n";
import RecentCartItem from "./RecentCartItem.component";

const NavbarComponent = (): React.JSX.Element => {
  const { totalItems } = useCart();
  const [expanded, setExpanded] = useState(false);
  const { t } = i18n;
  const langPrefix = getUrlLangPrefix();
  const homeUrl = getHomeUrl();
  const language = getCurrentLang();

  function getSwitchToLanguage() {
    return language === "en" ? "FR" : "EN";
  }

  const { pathname } = useLocation();
  const hideLinks =
    pathname.startsWith("/checkout") || pathname.startsWith("/fr/checkout");

  const handleLinkClick = () => {
    setExpanded(false);
  };

  function getSwitchToLink() {
    const counterpart = getCounterpartPath(pathname);
    if (counterpart) return counterpart + window.location.search;
    // Unknown path (not in ROUTE_PAIRS) — preserve the path, just toggle the /fr prefix.
    const toggled = pathname.startsWith("/fr/")
      ? pathname.slice(3)
      : `/fr${pathname}`;
    return toggled + window.location.search;
  }

  return (
    <Navbar expand="lg" expanded={expanded} sticky="top">
      <Container>
        {!hideLinks && (
          <Navbar.Toggle
            aria-controls="responsive-navbar-nav"
            onClick={() => setExpanded(!expanded)}
          />
        )}
        <Navbar.Brand as={NavLink} to={homeUrl}>
          <Image
            alt="PakVille™"
            height={39}
            src="/images/logo/pakville.svg"
            style={{ maxWidth: "150px" }}
            width={206}
          />
        </Navbar.Brand>

        {!hideLinks && (
          <Nav.Link
            as={NavLink}
            className="d-block d-lg-none nav-cart"
            onClick={() => handleLinkClick()}
            to={`${langPrefix}/${t("_route.cart")}`}
          >
            <img alt={t(`cart`)} src={FaCart} />
            <Badge pill bg="danger">
              {totalItems}
            </Badge>
          </Nav.Link>
        )}

        {!hideLinks && (
          <Navbar.Collapse
            className="justify-content-center"
            id="responsive-navbar-nav"
          >
            <Nav className="justify-content-center" style={{ flex: "2 0" }}>
              <Nav.Link
                end
                as={NavLink}
                onClick={() => handleLinkClick()}
                to={homeUrl}
              >
                {t("home")}
              </Nav.Link>

              <Nav.Link
                as={NavLink}
                onClick={() => handleLinkClick()}
                to={`${langPrefix}/${t("_route.the-technology")}`}
              >
                {t("the_technology")}
              </Nav.Link>

              <NavDropdown
                className="navDropdown"
                id="nav-models-dropdown"
                title={t("Home Kits")}
              >
                <NavDropdown.Item
                  as={NavLink}
                  onClick={() => handleLinkClick()}
                  to={`${langPrefix}/${t("_route.models")}/overview`}
                >
                  {t("model_overview")}
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={NavLink}
                  onClick={() => handleLinkClick()}
                  to={`${langPrefix}/${t("_route.models")}/PakMonaco`}
                >
                  PakMonaco
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={NavLink}
                  onClick={() => handleLinkClick()}
                  to={`${langPrefix}/${t("_route.models")}/PakSamara`}
                >
                  PakSamara
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={NavLink}
                  onClick={() => handleLinkClick()}
                  to={`${langPrefix}/${t("_route.models")}/PakTokyo`}
                >
                  PakTokyo
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={NavLink}
                  onClick={() => handleLinkClick()}
                  to={`${langPrefix}/${t("_route.models")}/PakAlps`}
                >
                  PakAlps
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={NavLink}
                  onClick={() => handleLinkClick()}
                  to={`${langPrefix}/${t("_route.models")}/PakNewYork`}
                >
                  PakNewYork
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={NavLink}
                  onClick={() => handleLinkClick()}
                  to={`${langPrefix}/${t("_route.models")}/PakShiraz`}
                >
                  PakShiraz
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={NavLink}
                  onClick={() => handleLinkClick()}
                  to={`${langPrefix}/${t("_route.models")}/PakMontreal`}
                >
                  PakMontréal
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={NavLink}
                  onClick={() => handleLinkClick()}
                  to={`${langPrefix}/${t("_route.models")}/PakStockholm`}
                >
                  PakStockholm
                </NavDropdown.Item>
              </NavDropdown>

              <Nav.Link
                as={NavLink}
                onClick={() => handleLinkClick()}
                to={`${langPrefix}/${t("_route.multi-storey-buildings")}`}
              >
                {t("Multi-Storey Buildings")}
              </Nav.Link>

              <Nav.Link
                as={NavLink}
                onClick={() => handleLinkClick()}
                to={`${langPrefix}/${t("_route.projects")}`}
              >
                {t("Projects")}
              </Nav.Link>

              <Nav.Link
                as={NavLink}
                onClick={() => handleLinkClick()}
                to={`${langPrefix}/${t("_route.contact-us")}`}
              >
                {t("contact_us")}
              </Nav.Link>

              <NavDropdown
                className="navDropdown"
                id="nav-models-dropdown"
                title={t("see_more")}
              >
                <NavDropdown.Item
                  as={NavLink}
                  onClick={() => handleLinkClick()}
                  to={`${langPrefix}/${t("_route.community-partnerships")}`}
                >
                  {t("Community Partnerships")}
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={NavLink}
                  onClick={() => handleLinkClick()}
                  to={`${langPrefix}/${t("_route.media-coverage")}`}
                >
                  {t("media_coverage")}
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={NavLink}
                  onClick={() => handleLinkClick()}
                  to={`${langPrefix}/${t("_route.awards")}`}
                >
                  {t("Awards")}
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={NavLink}
                  onClick={() => handleLinkClick()}
                  to={`${langPrefix}/${t("_route.blog")}`}
                >
                  {t("blog")}
                </NavDropdown.Item>
                <NavDropdown.Item
                  as={NavLink}
                  onClick={() => handleLinkClick()}
                  to={`${langPrefix}/${t("_route.who-we-are")}`}
                >
                  {t("Who We Are")}
                </NavDropdown.Item>
              </NavDropdown>
            </Nav>

            <Nav className="justify-content-center ms-auto align-items-start align-items-lg-center ">
              <Nav.Link
                as={NavLink}
                className="m-0 p-0 pe-2"
                onClick={() => handleLinkClick()}
                to={`${langPrefix}/${t("_route.shop")}`}
              >
                <Button
                  className="p-1 ps-2 pe-2"
                  size="sm"
                  style={{ fontSize: "1rem" }}
                  variant="primary"
                >
                  {t("shop")}
                </Button>
              </Nav.Link>

              <Nav.Link
                as={NavLink}
                className="d-none d-lg-block nav-cart"
                onClick={() => handleLinkClick()}
                to={`${langPrefix}/${t("_route.cart")}`}
              >
                <img alt={t(`cart`)} src={FaCart} />
                <Badge pill bg="danger">
                  {totalItems}
                </Badge>
              </Nav.Link>

              <Nav.Link
                as={NavLink}
                onClick={() => handleLinkClick()}
                to={getSwitchToLink()}
              >
                {getSwitchToLanguage()}
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        )}

        <RecentCartItem />
      </Container>
    </Navbar>
  );
};

export default NavbarComponent;
